/**
 * Banking access requests — /naytive-banking-access
 *
 * Founder 22 Oct 2026: business accounts are invite-only. In the iOS app every
 * "open an account" entry now opens a "Request access" sheet; each request
 * lands here (banking_access_requests, one per member) and r@naytive.com is
 * emailed with a link to `?id=<request>`. Ops reaches out, then moves the
 * request through new → contacted → invited / declined, with notes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { naytiveAPI } from '../services/api';

const STATUSES = ['new', 'contacted', 'invited', 'declined'];
const STATUS_COLORS = {
  new: { bg: '#FEF3C7', fg: '#92400E' },
  contacted: { bg: '#E0E7FF', fg: '#3730A3' },
  invited: { bg: '#D1FAE5', fg: '#065F46' },
  declined: { bg: '#FEE2E2', fg: '#991B1B' },
};

/** Where in the app the member tapped (v3 SOURCES, short form). */
const SOURCES = {
  money_card: 'Money · account card',
  money_more: 'Money · More sheet',
  home_balance: 'Home · Setup bank account',
  home_money_sheet: 'Home · Money sheet',
  home_needs: 'Home / Tasks · Needs you card',
  tasks: 'Tasks',
  connect_business: 'Connect · business account',
  business_account: 'Business account · company',
  business_start: 'Business application link',
  money_apply: 'Open account link',
  money_resolve: 'Account / Send / Cards screen',
};

export default function NaytiveBankingAccess() {
  const [params] = useSearchParams();
  const focusId = Number(params.get('id')) || null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});
  const [toast, setToast] = useState(null);
  const focusRow = useRef(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2400); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listBankingAccessRequests(filter === 'all' ? {} : { status: filter });
      setItems(r.data?.items || []);
    } catch (e) {
      console.error('[bankingAccess] load failed', e);
      flash(e.response?.data?.message || 'Could not load requests');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // From the ops email: scroll to the request it names.
  useEffect(() => {
    if (focusId && focusRow.current) focusRow.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, items]);

  const patch = async (id, body, ok) => {
    setBusyId(id);
    try {
      const r = await naytiveAPI.updateBankingAccessRequest(id, body);
      const item = r.data?.item;
      if (item) setItems((list) => list.map((x) => (x.id === id ? item : x)));
      else await load();
      if (ok) flash(ok);
      return true;
    } catch (e) {
      flash(e.response?.data?.message || 'Update failed');
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const saveNotes = async (r) => {
    const value = notes[r.id];
    if (value === undefined) return;
    if (await patch(r.id, { notes: value }, 'Notes saved')) {
      setNotes((d) => { const n = { ...d }; delete n[r.id]; return n; });
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of STATUSES) c[s] = items.filter((i) => i.status === s).length;
    return c;
  }, [items]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ padding: '24px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, color: '#111827' }}>Banking access requests</h1>
              <div style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>
                Members who tapped "Request access" in the app while business accounts are invite-only. Reach out, then update the status.
              </div>
            </div>
            <button style={btnGhost} onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
          </div>

          <div style={{ display: 'flex', gap: 6, margin: '16px 0', flexWrap: 'wrap' }}>
            {['all', ...STATUSES].map((s) => (
              <button key={s} onClick={() => setFilter(s)} style={{ ...chipBtn, ...(filter === s ? chipActive : null) }}>
                {s === 'all' ? 'All' : s}
                {filter === 'all' || filter === s ? <span style={{ opacity: 0.6, marginLeft: 6 }}>{counts[s] ?? 0}</span> : null}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={emptyBox}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={emptyBox}>No requests{filter !== 'all' ? ` in "${filter}"` : ''} yet.</div>
          ) : (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'auto', background: '#fff' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', textAlign: 'left', color: '#6B7280' }}>
                    <th style={th}>Member</th>
                    <th style={th}>Email</th>
                    <th style={th}>Companies</th>
                    <th style={th}>Account type</th>
                    <th style={th}>Requested at</th>
                    <th style={th}>Status</th>
                    <th style={th}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const pal = STATUS_COLORS[r.status] || {};
                    const draft = notes[r.id];
                    const focused = r.id === focusId;
                    return (
                      <tr
                        key={r.id}
                        ref={focused ? focusRow : undefined}
                        style={{ borderTop: '1px solid #F3F4F6', verticalAlign: 'top', background: focused ? '#FFFBEB' : undefined }}
                      >
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{r.user_name || `User #${r.user_id}`}</div>
                          <div style={sub}>User #{r.user_id}{r.user_phone ? ` · ${r.user_phone}` : ''}</div>
                          <div style={sub}>From: {SOURCES[r.source] || r.source || '—'}</div>
                        </td>
                        <td style={td}>{r.user_email || '—'}</td>
                        <td style={{ ...td, maxWidth: 260 }}>
                          {r.companies || <span style={{ color: '#9CA3AF' }}>None on file</span>}
                          {r.entity_name ? <div style={sub}>Asked for: {r.entity_name}</div> : null}
                        </td>
                        <td style={{ ...td, textTransform: 'capitalize' }}>{r.account_type}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {fmt(r.created_at)}
                          <div style={sub}>{r.notified_at ? 'Ops emailed' : 'Not emailed'}</div>
                        </td>
                        <td style={td}>
                          <span style={{ ...statusPill, background: pal.bg || '#F3F4F6', color: pal.fg || '#374151' }}>{r.status}</span>
                          <div style={{ marginTop: 8 }}>
                            <select
                              value={r.status}
                              disabled={busyId === r.id}
                              onChange={(e) => patch(r.id, { status: e.target.value }, `Marked ${e.target.value}`)}
                              style={select}
                              aria-label="Change status"
                            >
                              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                        </td>
                        <td style={{ ...td, minWidth: 240 }}>
                          <textarea
                            value={draft !== undefined ? draft : r.notes || ''}
                            onChange={(e) => setNotes((d) => ({ ...d, [r.id]: e.target.value }))}
                            placeholder="Add notes…"
                            rows={3}
                            style={textarea}
                          />
                          <button
                            style={{ ...btnGhost, marginTop: 6 }}
                            disabled={busyId === r.id || draft === undefined || draft === (r.notes || '')}
                            onClick={() => saveNotes(r)}
                          >
                            Save notes
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  );
}

function fmt(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const btnGhost = { padding: '7px 13px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const chipBtn = { padding: '6px 13px', border: '1px solid #E5E7EB', borderRadius: 999, background: '#fff', color: '#6B7280', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' };
const chipActive = { background: '#EEF2FF', color: '#2540D9', borderColor: '#C7D2FE' };
const th = { padding: '11px 14px', fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td = { padding: '12px 14px', color: '#374151' };
const sub = { fontSize: 12, color: '#6B7280', marginTop: 2 };
const statusPill = { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, textTransform: 'capitalize' };
const select = { fontSize: 12.5, padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 7, background: '#fff', textTransform: 'capitalize' };
const textarea = { width: '100%', boxSizing: 'border-box', padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 12.5, fontFamily: 'inherit', resize: 'vertical' };
const emptyBox = { padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', marginTop: 4 };
const toastStyle = { position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 200 };
